import { useMemo } from 'react';
import { useCategoryBudgets } from './useCategoryBudgets';
import { useTransactions } from './useTransactions';

export interface BudgetAlert {
  category: string;
  categoryLabel: string;
  limit: number;
  spent: number;
  percentage: number;
  status: 'ok' | 'warning' | 'exceeded';
  remaining: number;
}

const categoryLabels: Record<string, string> = {
  food: 'Alimentación',
  transport: 'Transporte',
  entertainment: 'Entretenimiento',
  health: 'Salud',
  education: 'Educación',
  clothing: 'Ropa',
  personal_care: 'Cuidado personal',
  gifts: 'Regalos',
  subscriptions: 'Suscripciones',
  other: 'Otros',
  supermercado: 'Supermercado',
  restaurantes: 'Restaurantes',
  belleza: 'Belleza',
  mascotas: 'Mascotas',
  hogar: 'Hogar',
};

export function useBudgetAlerts(month?: number, year?: number) {
  const { budgets } = useCategoryBudgets();
  const { transactions } = useTransactions();

  const now = new Date();
  const targetMonth = month ?? (now.getMonth() + 1);
  const targetYear = year ?? now.getFullYear();

  const alerts = useMemo(() => {
    if (!budgets?.length || !transactions?.length) return [];

    // Filter transactions for the target month/year
    const monthlyTransactions = transactions.filter(t => {
      const txDate = new Date(t.date);
      return (
        txDate.getMonth() + 1 === targetMonth &&
        txDate.getFullYear() === targetYear &&
        (t.type === 'variable_expense' || t.type === 'fixed_expense')
      );
    });

    // Calculate spending per category
    const spendingByCategory: Record<string, number> = {};
    monthlyTransactions.forEach(t => {
      const cat = t.category.toLowerCase();
      spendingByCategory[cat] = (spendingByCategory[cat] || 0) + Number(t.amount);
    });

    // Generate alerts for each budget
    const budgetAlerts: BudgetAlert[] = budgets
      .filter(b => b.is_active)
      .map(budget => {
        const cat = budget.category.toLowerCase();
        const spent = spendingByCategory[cat] || 0;
        const percentage = budget.monthly_limit > 0 ? (spent / budget.monthly_limit) * 100 : 0;
        const remaining = budget.monthly_limit - spent;

        let status: 'ok' | 'warning' | 'exceeded' = 'ok';
        if (percentage >= 100) {
          status = 'exceeded';
        } else if (percentage >= 80) {
          status = 'warning';
        }

        return {
          category: budget.category,
          categoryLabel: categoryLabels[cat] || budget.category,
          limit: budget.monthly_limit,
          spent,
          percentage,
          status,
          remaining,
        };
      })
      .sort((a, b) => b.percentage - a.percentage);

    return budgetAlerts;
  }, [budgets, transactions, targetMonth, targetYear]);

  const warningAlerts = alerts.filter(a => a.status === 'warning');
  const exceededAlerts = alerts.filter(a => a.status === 'exceeded');
  const hasAlerts = warningAlerts.length > 0 || exceededAlerts.length > 0;

  return {
    alerts,
    warningAlerts,
    exceededAlerts,
    hasAlerts,
  };
}
